import React, { useState } from 'react';
import { apiService } from '../../services/api';
import '../../App.css';

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

export const SubmitPosterModal = ({ isOpen, onClose, userLocation }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    startDate: '',
    endDate: '',
    address: '',
    latitude: null,
    longitude: null,
    imageUrl: null
  });

  const [addressResults, setAddressResults] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState('');

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      url: '',
      startDate: '',
      endDate: '',
      address: '',
      latitude: null,
      longitude: null,
      imageUrl: null
    });
    setAddressResults([]);
    setErrors({});
    setSubmitMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleAddressKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!formData.address.trim()) return;

      setIsSearchingAddress(true);
      setAddressResults([]);
      setFormData(prev => ({ ...prev, latitude: null, longitude: null }));

      try {
        // Build Nominatim URL with optional location bias
        let nominatimUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(formData.address)}&format=json&limit=5`;

        // If we have user location, add viewbox to bias results (~10 mile radius)
        if (userLocation?.latitude && userLocation?.longitude) {
          const offset = 0.15; // roughly 10 miles in degrees
          const viewbox = [
            userLocation.longitude - offset, // left
            userLocation.latitude + offset,  // top
            userLocation.longitude + offset, // right
            userLocation.latitude - offset   // bottom
          ].join(',');
          nominatimUrl += `&viewbox=${viewbox}&bounded=0`;
        }

        const response = await fetch(
          nominatimUrl,
          {
            headers: {
              'User-Agent': 'CorkSocialApp/1.0'
            }
          }
        );

        if (!response.ok) throw new Error('Address search failed');

        const results = await response.json();
        if (results.length === 0) {
          setErrors(prev => ({ ...prev, address: 'No addresses found. Try a more specific address.' }));
        } else {
          setAddressResults(results);
        }
      } catch (error) {
        console.error('Address search error:', error);
        setErrors(prev => ({ ...prev, address: 'Failed to search address. Please try again.' }));
      } finally {
        setIsSearchingAddress(false);
      }
    }
  };

  const selectAddress = (result) => {
    setFormData(prev => ({
      ...prev,
      address: result.display_name,
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon)
    }));
    setAddressResults([]);
    setErrors(prev => ({ ...prev, address: null }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      setErrors(prev => ({ ...prev, image: 'Image must be less than 10MB' }));
      e.target.value = '';
      return;
    }

    setIsUploadingImage(true);
    setErrors(prev => ({ ...prev, image: null }));

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: uploadData
        }
      );

      if (!response.ok) throw new Error('Image upload failed');

      const data = await response.json();
      setFormData(prev => ({
        ...prev,
        imageUrl: data.secure_url
      }));
    } catch (error) {
      console.error('Image upload error:', error);
      setErrors(prev => ({ ...prev, image: 'Failed to upload image. Please try again.' }));
      e.target.value = '';
    } finally {
      setIsUploadingImage(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (formData.endDate && formData.startDate) {
      if (new Date(formData.endDate) < new Date(formData.startDate)) {
        newErrors.endDate = 'End date must be on or after start date';
      }
    }

    if (!formData.latitude || !formData.longitude) {
      newErrors.address = 'Please search and select an address from the results';
    }

    if (!formData.imageUrl) {
      newErrors.image = 'Please upload a poster image';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check honeypot
    const honeypotValue = new FormData(e.target).get('website');
    if (honeypotValue) {
      setSubmitMessage('Thank you for your submission!');
      return;
    }

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      await apiService.submitPoster({
        title: formData.title,
        description: formData.description,
        url: formData.url,
        startDate: formData.startDate,
        endDate: formData.endDate || null,
        address: formData.address,
        latitude: formData.latitude,
        longitude: formData.longitude,
        imageUrl: formData.imageUrl
      });

      setSubmitMessage('Poster submitted successfully! It will appear after review.');
      resetForm();
    } catch (error) {
      console.error('Submit error:', error);
      if (error.message.includes('Too many')) {
        setSubmitMessage('Too many submissions. Please try again later.');
      } else {
        setSubmitMessage('Failed to submit poster. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <div className="submit-icon" onClick={onClose}>
        +
      </div>
    );
  }

  return (
    <>
      <div className="submit-icon" onClick={onClose}>+</div>
      <div className="help-overlay" onClick={onClose}>
        <div className="submit-popup" onClick={e => e.stopPropagation()}>
          <div className="help-close" onClick={onClose}>×</div>
          <h2 className="submit-title">Submit a Poster</h2>

          <form onSubmit={handleSubmit} className="submit-form">
            {/* Honeypot field */}
            <input
              type="text"
              name="website"
              style={{ display: 'none' }}
              tabIndex={-1}
              autoComplete="off"
            />

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                name="title"
                id="title"
                value={formData.title}
                onChange={handleInputChange}
                className="submit-input"
                placeholder="Event or business name"
              />
              {errors.title && <span className="error-message">{errors.title}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                name="description"
                id="description"
                value={formData.description}
                onChange={handleInputChange}
                className="submit-textarea"
                placeholder="Optional details about your event or business"
              />
            </div>

            <div className="form-group">
              <label htmlFor="url">Event URL</label>
              <input
                type="url"
                name="url"
                id="url"
                value={formData.url}
                onChange={handleInputChange}
                className="submit-input"
                placeholder="https://..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  id="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="submit-input"
                />
                {errors.startDate && <span className="error-message">{errors.startDate}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  name="endDate"
                  id="endDate"
                  min={formData.startDate}
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className="submit-input"
                />
                {errors.endDate && <span className="error-message">{errors.endDate}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address">Address *</label>
              <input
                type="text"
                name="address"
                id="address"
                value={formData.address}
                onChange={handleInputChange}
                onKeyDown={handleAddressKeyDown}
                className="submit-input"
                placeholder="Enter address and press Enter to search"
              />
              {isSearchingAddress && <span className="searching-text">Searching...</span>}
              {formData.latitude && formData.longitude && (
                <span className="address-confirmed">Address confirmed</span>
              )}
              {errors.address && <span className="error-message">{errors.address}</span>}

              {addressResults.length > 0 && (
                <ul className="address-results">
                  {addressResults.map((result, index) => (
                    <li key={index} onClick={() => selectAddress(result)}>
                      {result.display_name}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="image">Poster Image * (max 10MB)</label>
              <input
                type="file"
                name="image"
                id="image"
                accept="image/*"
                onChange={handleImageChange}
                className="submit-input submit-file-input"
                disabled={isUploadingImage}
              />
              {isUploadingImage && <span className="searching-text">Uploading...</span>}
              {errors.image && <span className="error-message">{errors.image}</span>}

              {formData.imageUrl && (
                <img src={formData.imageUrl} alt="Preview" className="image-preview" />
              )}
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting || isUploadingImage}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Poster'}
            </button>

            {submitMessage && (
              <p className={`submit-message ${submitMessage.includes('success') ? 'success' : 'error'}`}>
                {submitMessage}
              </p>
            )}
          </form>
        </div>
      </div>
    </>
  );
};
